import { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import * as yup from "yup";

import MyButton from "../../../components/MyButton";
import MyTextField from "../../../components/inputs/MyTextField";
import MyInputFile from "../../../components/inputs/MyInputFile";
import MyEditor from "../../../components/inputs/MyEditor";
import { doUploadFiles, doUploadSingleFile } from "../../../utils/file";
import { useFile } from "../../../hooks/useFile";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { SiteContext } from "../../../context/SiteProvider";
import { AuthContext } from "../../../context/AuthProvider";
import { renderBody } from "../../../utils";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";

const NewsUpsert01 = ({ boardCode, parent = null, hasVideoUrl }) => {
  const navigate = useNavigate();
  const { itemNo } = useParams();
  const { getFileUploadUrl } = useFile();
  const {
    createBoardItemMutation,
    updateBoardItemMutation,
    useGetBoardItem,
    useGetBoard,
  } = useBoardItem();
  const queryClient = useQueryClient();
  const { setLoadingScreen } = useContext(SiteContext);
  const { profile, roleCode, isAdmin, isAuthenticated } =
    useContext(AuthContext);

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading } = useGetBoardItem(
    {
      boardCode: board?.boardCode,
      itemNo,
      enabled:
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.READ,
          nonPermissions,
          isAdmin
        ) &&
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.WRITE,
          nonPermissions,
          isAdmin
        ),
    },
    (data) => data.data
  );

  const initValues = {
    title: "",
    body: "",
    files: [],
    videoUrl: "",
    embeddedFiles: [],
  };

  const validationSchema = yup.object({
    title: yup.string().required("필수 입력 항목입니다."),
    files: yup
      .array()
      .of(yup.mixed().required("파일을 선택해 주세요."))
      .max(5, "최대 5개의 파일만 업로드할 수 있습니다.")
      .nullable(),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (vals) => {
    if (
      checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.WRITE,
        nonPermissions,
        isAdmin
      )
    ) {
      setLoadingScreen(true);
      let uploadedFiles = [];
      if (vals.files?.length) {
        uploadedFiles = await doUploadFiles(vals.files, getFileUploadUrl, {
          boardCode: board?.boardCode,
        });
      }

      const payload = {
        title: vals.title,
        body: vals.body,
        additionalItems: {},
        userId: data?.writer?.userId ? data.writer.userId : profile.userId,
        attachedFiles: uploadedFiles.map((file) => ({
          fileName: file.name,
          fileId: file.fileId,
        })),
      };

      if (hasVideoUrl) payload.additionalItems.videoUrl = vals.videoUrl;

      let embeddedFiles = [];

      if (vals?.embeddedFiles?.length) {
        const content = document.querySelector("#body .ql-editor");
        if (content) {
          const clonedContent = content.cloneNode(true);
          for await (const embeddedFile of vals.embeddedFiles) {
            const file = await doUploadSingleFile(
              embeddedFile,
              getFileUploadUrl,
              {
                boardCode: board?.boardCode,
              }
            );

            const imgId = embeddedFile.fileId;
            const img = clonedContent.querySelector(`img#${CSS.escape(imgId)}`);

            if (img) {
              img.setAttribute("src", file.fileId);
              img.setAttribute("id", file.fileId);
            }

            embeddedFiles.push({
              fileName: file.name,
              fileId: file.fileId,
            });
          }
          payload.body = clonedContent.innerHTML;
          payload.embeddedFiles = embeddedFiles;
        }
      }

      if (!itemNo) {
        createBoardItemMutation.mutate(
          { boardCode: board.boardCode, payload },
          {
            onSuccess: (res) => {
              if (res) {
                methods.reset();
                alert("게시글 작성이 완료되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item?boardCode={boardCode}"],
                });
                navigate(parent ? parent : "/News/Notice");
              } else {
                alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
            },
          }
        );
      } else {
        updateBoardItemMutation.mutate(
          { boardCode: board.boardCode, itemNo, payload },
          {
            onSuccess: (res) => {
              if (res) {
                alert("게시물이 성공적으로 업데이트되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item/{boardCode}/{itemNo}"],
                });
                navigate(`${parent ? parent : "/News/Notice"}/${itemNo}`);
              } else {
                alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
            },
          }
        );
      }
    } else {
      alert(getPermissionMessage(BOARD_PERMISSION.WRITE));
    }
  };

  useEffect(() => {
    if (!isLoading && data) {
      methods.setValue("title", data.title);
      methods.setValue("body", renderBody(data.body, data.embeddedFiles));
      if (hasVideoUrl)
        methods.setValue("videoUrl", data.additionalItems?.videoUrl);

      if (data.attachedFiles?.length) {
        const newFiles = data.attachedFiles.map((file) => ({
          fileId: file.fileId,
          name: file.fileName,
          file: {
            ...file,
            type: file.contentType,
          },
        }));
        methods.setValue("files", newFiles);
      }

      if (data.embeddedFiles?.length) {
        const newFiles = data.embeddedFiles.map((file) => ({
          fileId: file.fileId,
          name: file.fileName,
          file: {
            ...file,
            type: file.contentType,
          },
        }));
        methods.setValue("embeddedFiles", newFiles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data]);

  useEffect(() => {
    if (
      !isBoardLoading &&
      !!board &&
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.WRITE,
        nonPermissions,
        isAdmin
      )
    )
      alert(getPermissionMessage(BOARD_PERMISSION.WRITE));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, isBoardLoading, roleCode, isAdmin]);

  return (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.WRITE}
      boardCode={boardCode}
    >
      <div className="border-t-2 border-primary md:pt-10 pt-6">
        <h2 className="font-semibold lg:text-3xl md:text-2xl text-xl mb-4">
          글 {!itemNo ? "작성" : "수정"}
        </h2>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="title"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                제목 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyTextField
                  id="title"
                  name="title"
                  placeholder="제목을 입력해주세요."
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="body"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                내용
              </label>
              <div className="flex-1">
                <MyEditor name="body" rows="8" cols="30" />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="files"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                파일 첨부
              </label>
              <div className="flex-1">
                <MyInputFile
                  name="files"
                  multiple
                  placeholder="파일을 첨부해 주세요."
                  hint="*파일 첨부는 최대 5개까지 업로드 가능합니다."
                />
              </div>
            </div>

            {hasVideoUrl && (
              <div className="flex flex-wrap md:mb-5 mb-3">
                <label
                  htmlFor="videoUrl"
                  className="md:mt-4 md:w-[150px] w-full font-semibold"
                >
                  동영상
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="videoUrl"
                    name="videoUrl"
                    placeholder="동영상 URL을 입력해주세요."
                  />
                </div>
              </div>
            )}

            <div className="flex items-center md:mt-10 mt-6 md:gap-4 gap-2">
              <MyButton
                variant="contained"
                color="#111111"
                className="font-semibold sm:min-w-[120px] mr-auto text-white"
                onClick={() =>
                  checkRoleAndPermissions(
                    roleCode,
                    board,
                    BOARD_PERMISSION.ACCESS,
                    nonPermissions,
                    isAdmin
                  )
                    ? navigate(parent ? parent : "/News/Notice")
                    : alert(getPermissionMessage(BOARD_PERMISSION.ACCESS))
                }
              >
                목록보기
              </MyButton>

              <MyButton
                variant="outlined"
                className="sm:min-w-[120px]"
                onClick={() => {
                  if (itemNo) {
                    checkRoleAndPermissions(
                      roleCode,
                      board,
                      BOARD_PERMISSION.READ,
                      nonPermissions,
                      isAdmin
                    )
                      ? navigate(
                          `${parent ? parent : "/News/Notice"}/${itemNo}`
                        )
                      : alert(getPermissionMessage(BOARD_PERMISSION.READ));
                  } else {
                    checkRoleAndPermissions(
                      roleCode,
                      board,
                      BOARD_PERMISSION.ACCESS,
                      nonPermissions,
                      isAdmin
                    )
                      ? navigate(parent ? parent : "/News/Notice")
                      : alert(getPermissionMessage(BOARD_PERMISSION.ACCESS));
                  }
                }}
              >
                <span className="font-semibold">취소</span>
              </MyButton>
              {!itemNo ||
              (!!itemNo &&
                isAuthenticated &&
                (profile?.userId === data?.writer?.userId || isAdmin)) ? (
                <MyButton
                  variant="contained"
                  color="primary"
                  className="font-semibold sm:min-w-[120px]"
                  type="submit"
                >
                  저장
                </MyButton>
              ) : null}
            </div>
          </form>
        </FormProvider>
      </div>
    </CheckRoleAndPermissionDisplay>
  );
};

export default NewsUpsert01;
